import * as React from "react";
import autobind from "autobind-decorator";
import styles from "./Table.scss";
import { reaction, observable, computed } from "mobx";
import { observer } from "mobx-react";
import { assign } from "lodash";

export interface IPoint {
	x: number;
	y: number;
}

export interface IArea {
	x0: number; y0: number;
	x1: number; y1: number;
}

export interface ITableColumnProps {
	rows: number;
	columns: number;

	fixedTop: number;
	fixedLeft: number;

	tableStyles?: string[];

	renderCell: (pos: IPoint, props: ITableCellProps) => React.ReactElement<ITableCellProps>;
	getRowHeight: (y: number) => number;
	getColumnWidth: (x: number) => number;
}

@observer
export class Table extends React.Component<ITableColumnProps, {}> {
	private renderCache = new Map<string, JSX.Element>();
	private offsetCache = new Map<string, number>();
	private hiddenCells = new Set<string>();
	private container: HTMLDivElement;
	private scrolled = false;
	private lastScrollCalc = +new Date();

	@observable
	private activeArea: IArea = { x0: 0, y0: 0, x1: 0, y1: 0 };
	@computed
	private get virtualized() {
		let totalsCells = this.props.columns * this.props.rows;
		return totalsCells > Infinity; // 7500;
		// TODO: needs fix for visible area calculation first
	}

	public componentDidMount() {
		// setup reaction to change of virtualized state
		reaction(
			() => this.virtualized,
			() => {
				if (this.virtualized) {
					// we need to start the requestAnimationFrame loop if virtualization becomes active
					requestAnimationFrame(this.onFrame);
				} else {
					// everything is active if we aren't virtualized
					this.activeArea = {
						x0: 0,
						y0: 0,
						x1: Infinity,
						y1: Infinity,
					};
				}
			},
			{ fireImmediately: true }
		);
	}

	/**
	 * Runs on each frame if `this.virtualized === true`.
	 * Updates `activeArea` which in turn triggers a `render()`
	 */
	@autobind
	private onFrame() {
		// exit the loop if we aren't virtualized
		if (!this.virtualized) { return; }

		let timeSinceLast = +new Date() - this.lastScrollCalc;
		if (timeSinceLast > 100 && this.scrolled) {
			this.scrolled = false;
			this.lastScrollCalc = +new Date();

			// TODO: sizing
			this.activeArea.x0 = Math.floor(this.container.scrollLeft / 100);
			this.activeArea.y0 = Math.floor(this.container.scrollTop / 20);
			this.activeArea.x1 = Math.ceil((this.container.scrollLeft + this.container.clientWidth) / 100);
			this.activeArea.y1 = Math.ceil((this.container.scrollTop + this.container.clientHeight) / 20) + 30;
		}

		requestAnimationFrame(this.onFrame);
	}

	/**
	 * Calculates the intersection (overlap) of two Areas
	 * @param a1 first Area
	 * @param a2 second Area
	 * @returns intersection of Area
	 */
	private areaIntersection(a1: IArea, a2: IArea): IArea {
		return {
			x0: Math.max(a1.x0, a2.x0), y0: Math.max(a1.y0, a2.y0),
			x1: Math.min(a1.x1, a2.x1), y1: Math.min(a1.y1, a2.y1),
		};
	}

	public render() {
		let topLeft = this.getCells({
			x0: 0,
			y0: 0,
			x1: this.props.fixedLeft,
			y1: this.props.fixedTop,
		});
		let top = this.getCells(this.areaIntersection({
			x0: this.activeArea.x0 + this.props.fixedLeft,
			y0: 0,
			x1: this.activeArea.x1,
			y1: this.props.fixedTop,
		}, {
			x0: this.props.fixedLeft,
			y0: 0,
			x1: this.props.columns,
			y1: this.props.fixedTop,
		}));
		let left = this.getCells(this.areaIntersection({
			x0: 0,
			y0: this.activeArea.y0 + this.props.fixedTop,
			x1: this.props.fixedLeft,
			y1: this.activeArea.y1,
		}, {
			x0: 0,
			y0: this.props.fixedTop,
			x1: this.props.fixedLeft,
			y1: this.props.rows,
		}));
		let content = this.getCells(this.areaIntersection({
			x0: this.activeArea.x0 + this.props.fixedLeft,
			y0: this.activeArea.y0 + this.props.fixedTop,
			x1: this.activeArea.x1,
			y1: this.activeArea.y1,
		}, {
			x0: this.props.fixedLeft,
			y0: this.props.fixedTop,
			x1: this.props.columns,
			y1: this.props.rows,
		}));
		let tableStyles = "";
		for (let i in this.props.tableStyles) {
			let s = this.props.tableStyles[i];
			if (styles.hasOwnProperty(s)) {
				tableStyles += styles[s] + " ";
			}
			else {
				throw ReferenceError(`'.${s}' is not available as table style class`);
			}
		}

		return (
			<div
				className={styles.container + " " + (tableStyles.length ? tableStyles : styles.defaultStyle)}
				ref={r => this.container = r}
				onScroll={e => this.scrolled = true}
				style={{ width: "100%", height: "100%" }}
			>
				<div className={styles.topLeft}>
					{...topLeft}
				</div>
				<div className={styles.top}>
					{...top}
				</div>
				<div className={styles.left}>
					{...left}
				</div>
				<div
					className={styles.content}
					style={{
						width: `${this.calculateOffset("x", this.props.columns)}px`,
						height: `${this.calculateOffset("y", this.props.rows)}px`
					}}
				>
					{...content}
				</div>
			</div>
		);
	}

	/**
	 * returns all cells in the given area
	 * @param area requested area
	 */
	private getCells(area: IArea): JSX.Element[] {
		let cells: JSX.Element[] = [];

		for (let y = area.y0; y < area.y1; y++) {
			for (let x = area.x0; x < area.x1; x++) {
				let cell = this.renderCell({ y, x });
				if (cell) {
					cells.push(cell);
				}
			}
		}

		return cells;
	}

	private renderCell(pos: IPoint): JSX.Element {
		let key = `${pos.x}y${pos.y}`;
		if (this.renderCache.has(key)) {
			return this.renderCache.get(key);
		}
		if (this.hiddenCells.has(key)) {
			return;
		}

		let cell: React.ReactElement<ITableCellProps>;
		let props: ITableCellProps = {
			table: this,
			pos: pos,
		};

		try {
			cell = this.props.renderCell(pos, props);
		} catch (e) {
			cell = <TableCell {...props}>
				{e.toString()}
				<pre>{JSON.stringify(e)}</pre>
			</TableCell>;
		}

		if (cell.props.colSpan > 1 || cell.props.rowSpan > 1) {
			// hidden area is 1 too big, that is handled by the loop
			let hiddenArea: IArea = {
				x0: pos.x,
				y0: pos.y,
				x1: pos.x + (cell.props.colSpan || 1),
				y1: pos.y + (cell.props.rowSpan || 1),
			};
			for (let x = hiddenArea.x0; x < hiddenArea.x1; x++) {
				for (let y = hiddenArea.y0; y < hiddenArea.y1; y++) {
					let hiddenKey = `${x}y${y}`;
					// don't hide ourself
					if (key !== hiddenKey) {
						this.hiddenCells.add(hiddenKey);
					}
				}
			}
		}

		this.renderCache.set(key, cell);
		return cell;
	}

	public calculateOffset(dir: "y" | "x", index: number): number {
		let key = `${dir},${index}`;
		if (this.offsetCache.has(key)) {
			return this.offsetCache.get(key);
		}
		if (index === 0) {
			return 0;
		}

		let offset = this.calculateOffset(dir, index - 1);
		if (dir === "x") {
			offset += this.props.getColumnWidth(index - 1);
		} else if (dir === "y") {
			offset += this.props.getRowHeight(index - 1);
		}

		this.offsetCache.set(key, offset);
		return offset;
	}

	public calculateCellOffset(pos: IPoint): IPoint {
		return {
			x: this.calculateOffset("x", pos.x),
			y: this.calculateOffset("y", pos.y),
		};
	}
}

export interface ITableCellProps {
	table: Table;
	pos: IPoint;

	style?: React.CSSProperties;
	className?: string;

	colSpan?: number;
	rowSpan?: number;
}

@observer
export class TableCell extends React.Component<ITableCellProps, {}> {
	public render() {
		// grab props
		let { table, pos, children } = this.props;
		let rowSpan = this.props.rowSpan || 1;
		let colSpan = this.props.colSpan || 1;

		// calculate some stuff
		let offset = table.calculateCellOffset(pos);

		let height = 0;
		for (let i = 0; i < rowSpan; i++) {
			height += table.props.getRowHeight(pos.y + i);
		}
		let width = 0;
		for (let i = 0; i < colSpan; i++) {
			width += table.props.getColumnWidth(pos.x + i);
		}

		// build style
		let style: React.CSSProperties = assign({
			position: "absolute",
			top: `${offset.y}px`,
			left: `${offset.x}px`,
			height: `${height}px`,
			width: `${width}px`,
			overflow: "hidden",
			lineHeight: "25px"
		}, this.props.style); // merge with props

		return <div key={`${pos.x},${pos.y}`} style={style} className={styles.cell + " " + this.props.className}>
			{children}
		</div>;
	}
}



// WEBPACK FOOTER //
// ./src/common/Table/Table.tsx